import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Share2 } from "lucide-react";
import { fetchArticle, fetchArticles } from "../lib/api";
import { formatDate } from "../lib/brand";
import ArticleCard from "../components/ArticleCard";
import SEO from "../components/SEO";

function renderBody(body) {
  // very small markdown-like: bold **x**, split paragraphs
  const paragraphs = body.split(/\n\n+/);
  return paragraphs.map((p, idx) => {
    const parts = [];
    let remaining = p;
    let key = 0;
    const re = /\*\*(.+?)\*\*/g;
    let match;
    let lastIndex = 0;
    while ((match = re.exec(p)) !== null) {
      if (match.index > lastIndex) parts.push(<span key={key++}>{p.slice(lastIndex, match.index)}</span>);
      parts.push(<strong key={key++}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < p.length) parts.push(<span key={key++}>{p.slice(lastIndex)}</span>);
    return <p key={idx}>{parts.length ? parts : p}</p>;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setErr(false);
    fetchArticle(slug).then((a) => {
      setArticle(a);
      fetchArticles({ category: a.category, limit: 4 }).then((r) => {
        setRelated((r.articles || []).filter((x) => x.slug !== slug).slice(0, 3));
      });
    }).catch(() => setErr(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: article?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch {}
  };

  if (err) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <div className="text-headline text-4xl">Story not found.</div>
      <Link to="/editorial" className="btn-rust mt-6 inline-flex">Back to Editorial</Link>
    </div>
  );

  if (!article) return <div className="py-24 text-center font-mono-ed uppercase tracking-widest text-[var(--muted)]">Loading…</div>;

  return (
    <article data-testid={`article-detail-${article.slug}`}>
      <SEO
        title={article.title}
        description={article.dek}
        image={article.hero_image}
        type="article"
        author={article.author}
        publishedAt={article.published_at}
      />
      {/* Top meta */}
      <div className="border-b-2 border-[var(--ink)]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
          <Link to="/editorial" className="inline-flex items-center gap-2 text-sm font-display uppercase tracking-widest hover:text-[var(--rust)]" data-testid="back-to-editorial">
            <ArrowLeft size={14} /> Back to Archive
          </Link>
          <button onClick={share} className="inline-flex items-center gap-2 text-sm font-display uppercase tracking-widest hover:text-[var(--rust)]" data-testid="article-share-button">
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Headline block */}
      <header className="border-b-2 border-[var(--ink)]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-16">
          <div className="tape mb-6">{article.category}</div>
          <h1 className="text-serif-headline text-4xl md:text-6xl lg:text-7xl text-[var(--ink)]">
            {article.title}
          </h1>
          <p className="text-dek text-xl md:text-2xl mt-5 max-w-3xl">{article.dek}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono-ed text-xs uppercase tracking-widest text-[var(--muted)]">
            <span>By <span className="text-[var(--ink)] font-semibold">{article.author}</span></span>
            <span>·</span>
            <span>{formatDate(article.published_at)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {article.read_minutes} min read</span>
          </div>
        </div>
      </header>

      {/* Hero image */}
      <div className="bg-[var(--ink)]">
        <div className="max-w-6xl mx-auto">
          <img src={article.hero_image} alt={article.title}
               loading="eager"
               className="w-full aspect-[21/9] object-cover" />
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 prose-editorial" data-testid="article-body">
        {renderBody(article.body)}

        <div className="mt-10 pt-8 border-t-2 border-[var(--ink)] flex flex-wrap gap-2">
          {article.tags?.map((t) => (
            <span key={t} className="chip text-xs">#{t}</span>
          ))}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t-2 border-[var(--ink)] py-14">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-center justify-between pb-4 mb-8 border-b-2 border-[var(--ink)]">
              <h2 className="text-headline text-3xl md:text-4xl">More from this beat</h2>
              <Link to="/editorial" className="nav-link">All Stories</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              {related.map((a) => <ArticleCard key={a.slug} article={a} size="sm" />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
